import { navigationEpoch } from '../../navigation/loadEpoch';
import { getPersistenceNamespace } from '../helpers/persistenceHelpers';
import type { StateCreator } from 'zustand';
import type {
  SavedModel,
  MetricInstance,
  MetricConfig,
  FilterGroup,
  QuerySelection,
  DataDomain,
  Perspective,
} from '../../types';
import type { VoyagerState } from '../useVoyagerStore';
import {
  getDomains,
  getPerspectives,
  getAllPerspectives,
  getModel,
  saveReport as apiSaveReport,
  updateReport as apiUpdateReport,
  getReport as apiGetReport,
  getReports as apiGetReports,
  deleteReport as apiDeleteReport,
} from '../../apis';
import { buildTreeFromModel, generateId, getCurrentUser } from '../../utils';
import {
  createEmptyFilterGroup,
  createDefaultFilterConditions,
  stripDefaultFilters,
  hasConditions,
  serializeFilterGroup,
  filtersToGroup,
  deserializeFilterGroup,
} from '../helpers/filterHelpers';

export interface ReportsSlice {
  reports: SavedModel[];
  loadedReportId: string | null;
  loadedReportName: string | null;

  saveReport: (name: string, description: string, options?: { isTemplate?: boolean; templateTags?: string[] }) => Promise<void>;
  overwriteReport: (id: string, name: string, description: string, options?: { isTemplate?: boolean; templateTags?: string[] }) => Promise<void>;
  loadReport: (id: string, signal?: AbortSignal) => Promise<boolean>;
  fetchReports: () => Promise<void>;
  deleteReport: (id: string) => Promise<void>;
}

export const createReportsSlice: StateCreator<
  VoyagerState, [], [], ReportsSlice
> = (set, get) => ({
  reports: [],
  loadedReportId: null,
  loadedReportName: null,

  saveReport: async (name: string, description: string, options?: { isTemplate?: boolean; templateTags?: string[] }) => {
    const {
      selectedDomain,
      selectedPerspective,
      selectedMetrics,
      selectedDimensions,
      metricConfigs,
      calculatedAttributes,
      filters,
      filterGroup,
      defaultFilterIds,
      dateRangePreset,
      dateRangeStart,
      dateRangeEnd,
      activeDateField,
    } = get();

    // A report is bound to its perspective; the domain rides along when the
    // perspective has one (#464 — a domainless perspective still saves).
    if (!selectedPerspective) {
      set({ error: 'No perspective selected to save' });
      return;
    }

    const createdBy = getCurrentUser() || 'anonymous';

    set({ isLoading: true, error: null });
    try {
      const configsArray = Object.values(metricConfigs).filter(
        (c) => c.alias || c.aggregationOverride || c.timePeriod || c.dateDimension
      );

      // Strip default filters before persisting (they come from the model, not user)
      const userFilterGroup = defaultFilterIds.size > 0
        ? stripDefaultFilters(filterGroup, defaultFilterIds)
        : filterGroup;

      // Serialize the nested filterGroup for storage
      const serializedFg = hasConditions(userFilterGroup)
        ? serializeFilterGroup(userFilterGroup)
        : undefined;

      const selection: QuerySelection = {
        perspective: selectedPerspective,
        metrics: selectedMetrics,
        dimensions: selectedDimensions,
        filters,
        aggregates: {},
        ...(serializedFg ? { filterGroup: serializedFg } : {}),
        ...(configsArray.length > 0 ? { metricConfigs: configsArray } : {}),
        ...(calculatedAttributes.length > 0 ? { calculatedAttributes } : {}),
        ...(dateRangePreset ? { dateRangePreset } : {}),
        ...(dateRangeStart ? { dateRangeStart } : {}),
        ...(dateRangeEnd ? { dateRangeEnd } : {}),
        ...(activeDateField ? { activeDateField } : {}),
      };

      const saved = await apiSaveReport({
        name,
        description,
        domain: selectedDomain || '',
        perspective: selectedPerspective,
        selection,
        created_by: createdBy,
        ...(options?.isTemplate ? { isTemplate: true, templateTags: options.templateTags || [] } : {}),
      });

      set({
        reports: [saved, ...get().reports],
        loadedReportId: options?.isTemplate ? null : saved.id,
        loadedReportName: options?.isTemplate ? null : saved.name,
        isLoading: false,
      });
    } catch (err: any) {
      set({
        error: err?.message || 'Failed to save report',
        isLoading: false,
      });
    }
  },

  overwriteReport: async (id: string, name: string, description: string, options?: { isTemplate?: boolean; templateTags?: string[] }) => {
    const {
      selectedDomain,
      selectedPerspective,
      selectedMetrics,
      selectedDimensions,
      metricConfigs,
      calculatedAttributes,
      filters,
      filterGroup,
      defaultFilterIds,
      dateRangePreset,
      dateRangeStart,
      dateRangeEnd,
      activeDateField,
    } = get();

    // A report is bound to its perspective; the domain rides along when the
    // perspective has one (#464 — a domainless perspective still saves).
    if (!selectedPerspective) {
      set({ error: 'No perspective selected to save' });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const configsArray = Object.values(metricConfigs).filter(
        (c) => c.alias || c.aggregationOverride || c.timePeriod || c.dateDimension
      );

      // Strip default filters before persisting
      const userFilterGroup = defaultFilterIds.size > 0
        ? stripDefaultFilters(filterGroup, defaultFilterIds)
        : filterGroup;

      const serializedFg = hasConditions(userFilterGroup)
        ? serializeFilterGroup(userFilterGroup)
        : undefined;

      const selection: QuerySelection = {
        perspective: selectedPerspective,
        metrics: selectedMetrics,
        dimensions: selectedDimensions,
        filters,
        aggregates: {},
        ...(serializedFg ? { filterGroup: serializedFg } : {}),
        ...(configsArray.length > 0 ? { metricConfigs: configsArray } : {}),
        ...(calculatedAttributes.length > 0 ? { calculatedAttributes } : {}),
        ...(dateRangePreset ? { dateRangePreset } : {}),
        ...(dateRangeStart ? { dateRangeStart } : {}),
        ...(dateRangeEnd ? { dateRangeEnd } : {}),
        ...(activeDateField ? { activeDateField } : {}),
      };

      const updated = await apiUpdateReport(id, {
        name,
        description,
        domain: selectedDomain || '',
        perspective: selectedPerspective,
        selection,
        ...(options?.isTemplate !== undefined ? { isTemplate: options.isTemplate } : {}),
        ...(options?.templateTags !== undefined ? { templateTags: options.templateTags } : {}),
      });

      // Update in the local reports list
      set({
        reports: get().reports.map((r) => (r.id === id ? updated : r)),
        loadedReportId: updated.id,
        loadedReportName: updated.name,
        isLoading: false,
      });
    } catch (err: any) {
      set({
        error: err?.message || 'Failed to update report',
        isLoading: false,
      });
    }
  },

  loadReport: async (id: string, signal?: AbortSignal) => {
    const namespace = getPersistenceNamespace();
    const loadEpoch = navigationEpoch();
    set({ isLoading: true, error: null });
    try {
      const saved = await apiGetReport(id);
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return false;

      // Fetch domains list and perspectives for the saved domain so both dropdowns populate.
      // Without the domain layer (#464) the perspective list is the whole catalog
      // and the domain is whatever the perspective says.
      // A report saved without a domain (#464) still names a perspective, and
      // the perspective says which domain it is in.
      const { domains: domainsOn } = await get().ensureFeatures();
      let domain = saved.domain || '';
      let domains: DataDomain[] = [];
      let perspectives: Perspective[] = [];
      if (domainsOn && domain) {
        // the layer on, a report that names its domain: as it always was
        [domains, perspectives] = await Promise.all([getDomains(), getPerspectives(domain)]);
      } else {
        const [all, catalog] = await Promise.all([domainsOn ? getDomains() : Promise.resolve([]), getAllPerspectives()]);
        domains = all;
        domain = catalog.find((p) => p.id === saved.perspective)?.domain || '';
        perspectives = domainsOn ? catalog.filter((p) => p.domain === domain) : catalog;
      }

      // Load the semantic model FIRST: a report whose perspective is gone
      // must leave whatever was open exactly as it was.
      const model = await getModel(saved.perspective);
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return false;
      const treeNodes = buildTreeFromModel(model);

      // Set domains, domain, perspective, and perspectives list
      set({
        domains,
        selectedDomain: domain || null,
        selectedPerspective: saved.perspective,
        perspectives,
      });

      const expanded = new Set<string>();
      treeNodes.forEach((root) => {
        expanded.add(root.id);
      });

      // Restore selectedMetrics with backward compat (old format: string[], new format: MetricInstance[])
      const rawMetrics = saved.selection?.metrics || [];
      const restoredMetrics: MetricInstance[] = rawMetrics.map((m: string | MetricInstance) => {
        if (typeof m === 'string') {
          return { id: generateId(), metricName: m };
        }
        return m as MetricInstance;
      });

      // Restore metricConfigs from saved data
      const restoredConfigs: Record<string, MetricConfig> = {};
      if (saved.selection?.metricConfigs) {
        const isOldFormat = rawMetrics.length > 0 && typeof rawMetrics[0] === 'string';
        for (const c of saved.selection.metricConfigs) {
          if (isOldFormat) {
            // Old format: match config to instance by metricName
            const instance = restoredMetrics.find((m) => m.metricName === c.metricName);
            if (instance) {
              restoredConfigs[instance.id] = { ...c, instanceId: instance.id };
            }
          } else {
            // New format: configs have instanceId
            const key = c.instanceId || c.metricName;
            restoredConfigs[key] = c;
          }
        }
      }

      // Restore filterGroup: prefer stored filterGroup, fall back to legacy flat filters
      let restoredFilterGroup: FilterGroup;
      if (saved.selection?.filterGroup) {
        restoredFilterGroup = deserializeFilterGroup(saved.selection.filterGroup);
      } else if (saved.selection?.filters?.length) {
        restoredFilterGroup = filtersToGroup(saved.selection.filters);
      } else {
        restoredFilterGroup = createEmptyFilterGroup();
      }

      // Inject default filters as locked conditions (prepended before user filters)
      let defaultFilterIds = new Set<string>();
      if (model.default_filters && model.default_filters.length > 0) {
        const { conditions, ids } = createDefaultFilterConditions(model.default_filters);
        restoredFilterGroup = {
          ...restoredFilterGroup,
          children: [...conditions, ...restoredFilterGroup.children],
        };
        defaultFilterIds = ids;
      }

      // Templates load as unsaved copies (no loadedReportId/Name)
      const isTemplate = !!saved.isTemplate;

      set({
        semanticModel: model,
        treeNodes,
        expandedNodes: expanded,
        selectedMetrics: restoredMetrics,
        selectedDimensions: saved.selection?.dimensions || [],
        metricConfigs: restoredConfigs,
        calculatedAttributes: saved.selection?.calculatedAttributes || [],
        filters: saved.selection?.filters || [],
        filterGroup: restoredFilterGroup,
        defaultFilterIds,
        dateRangePreset: saved.selection?.dateRangePreset || 'YESTERDAY',
        dateRangeStart: saved.selection?.dateRangeStart || null,
        dateRangeEnd: saved.selection?.dateRangeEnd || null,
        activeDateField: saved.selection?.activeDateField || model.date_field || null,
        querySQL: '',
        queryResults: null,
        activeResultTab: null,
        loadedReportId: isTemplate ? null : saved.id,
        loadedReportName: isTemplate ? null : saved.name,
        isLoading: false,
      });
      return true;
    } catch (err: any) {
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return false;
      set({
        error: err?.message || 'Failed to load report',
        isLoading: false,
      });
      return false;
    }
  },

  fetchReports: async () => {
    try {
      const reports = await apiGetReports();
      set({ reports });
    } catch (err: any) {
      console.error('Failed to fetch reports:', err?.message);
    }
  },

  deleteReport: async (id: string) => {
    try {
      await apiDeleteReport(id);
      const { reports, loadedReportId } = get();
      const updates: Partial<VoyagerState> = {
        reports: reports.filter((r) => r.id !== id),
      };
      if (loadedReportId === id) {
        updates.loadedReportId = null;
        updates.loadedReportName = null;
      }
      set(updates);
    } catch (err: any) {
      set({ error: err?.message || 'Failed to delete report' });
    }
  },
});
